import { useTheme } from '@emotion/react'
import React, { FC, useState } from 'react'
import { useHistory } from 'react-router'
import { useRecaptcha } from '../../recaptcha'
import { useAuthActions } from '../../user-registration/authentication/AuthenticationProvider'
import { StyledLabel } from '../../user-registration/breach/components/RegistrationWall/components/RegistrationWallForm.styled'
import { themedValue } from '../../__styling'
import { MastheadPreference } from '../components/MastheadPreference'
import { EmailPreferenceItem } from '../components/PreferenceSection'
import {
    StyledPerthNowLogo,
    StyledTheWestLogo,
} from '../components/PreferenceSection.styled'
import { StyledButton } from '../components/Unsubscribe.styled'
import { getMastheadPreferences } from '../config'
import { PreferenceCardContext } from '../contexts/PreferenceCardContext'
import {
    StyledDivider,
    StyledEmailPreference,
    StyledFooter,
    StyledGreeting,
    StyledHeading,
    StyledLink,
    StyledMessageBody,
    StyledMessageBox,
    StyledWebLink,
} from '../EmailPreference.styled'
import { ListIdName, useGetAllLists } from '../hooks/useEmailPreferenceData'
import {
    StyledAsterisk,
    StyledBoldText,
    StyledDisclaimer,
    StyledError,
    StyledForm,
    StyledFormHeading,
    StyledFormSubHeading,
    StyledInput,
    StyledInputGroup,
    StyledSubmitButton,
    StyledSuccessBody,
    StyledText,
} from './EmailPreferenceNotLoggedIn.styled'

interface NewsletterSubscriptionFormState {
    firstName: string
    lastName: string
    email: string
    optInLists: ListIdName[]
}

export const EmailPreferenceNotLoggedIn = () => {
    const theme = useTheme()
    const { onLoginClick } = useAuthActions()

    const meta = themedValue(theme, {
        thewest: {
            logoGroup: [<StyledTheWestLogo />, <StyledPerthNowLogo />],
            privacyLink: '/terms',
            contactLink: '/contact',
        },
        perthnow: {
            logoGroup: [<StyledPerthNowLogo />, <StyledTheWestLogo />],
            privacyLink: '/terms-of-use',
            contactLink: '/contact-us',
        },
        sevennews: {
            logoGroup: [],
            privacyLink: '',
            contactLink: '/contact-us',
        },
        fallback: {
            logoGroup: [],
            privacyLink: '',
            contactLink: '/contact-us',
        },
    })

    const mastheadPreferences = getMastheadPreferences(theme)
    const allLists = useGetAllLists(mastheadPreferences)
    const [emailToken, setEmailToken] = useState<string>()
    const [formData, setFormData] = useState<NewsletterSubscriptionFormState>({
        email: '',
        firstName: '',
        lastName: '',
        optInLists: allLists,
    })

    const [formState, setformState] = useState<
        'ready' | 'submitting' | 'success' | 'error'
    >('ready')

    const ToggleHandler = async (
        checked: boolean,
        item: EmailPreferenceItem,
    ): Promise<void> => {
        const tempOptInList = [...formData.optInLists]

        if (checked) {
            tempOptInList.push(
                item.id
                    ? { listId: item.id, listName: item.name }
                    : { listId: '', listName: '' },
            )

            setFormData({
                ...formData,
                optInLists: tempOptInList,
            })
        } else {
            const filteredList = formData.optInLists.filter(
                (id) => id.listId !== item.id,
            )

            setFormData({
                ...formData,
                optInLists: filteredList,
            })
        }
    }

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        setFormData({
            ...formData,
            [e.target.name]: e.target.value.trim(),
        })
    }

    const { executeRecaptcha } = useRecaptcha()
    const [error, setError] = useState<string | null>(null)

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setformState('submitting')
        setError(null)
        const token = await executeRecaptcha({ action: 'subscribe' })

        const result = await fetch('/epc-newsletter-subscribe', {
            method: 'POST',
            body: JSON.stringify({
                payload: {
                    token,
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    optInLists: formData.optInLists.map((item) => item.listId),
                    requestToken: true,
                },
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const { token: responseEmailToken } = (await result.json()) as {
            token: string
        }

        if (result.status === 201) {
            setformState('success')
            setEmailToken(responseEmailToken)
            return
        }

        setformState('error')
        setError(await result.text())
    }

    if (formState === 'success') {
        return (
            <SuccessScreen
                newsletters={formData.optInLists.map((item) => item.listName)}
                email={formData.email}
                token={emailToken}
                contactLink={meta.contactLink}
            />
        )
    }

    return (
        <StyledEmailPreference>
            <StyledHeading>Subscribe to The West Newsletters</StyledHeading>
            <StyledMessageBody>
                <p>
                    Select the newsletters you want to receive,
                    <br />
                    then enter your details below to subscribe.
                </p>
                <StyledMessageBox>
                    If you are an existing subscriber, please&nbsp;
                    <StyledLink onClick={onLoginClick}>login</StyledLink> before
                    selecting your newsletters to ensure your preferences are
                    updated.
                </StyledMessageBox>
            </StyledMessageBody>

            <PreferenceCardContext.Provider
                value={{
                    toggleHandler: ToggleHandler,
                    defaultToggledOn: true,
                }}
            >
                {mastheadPreferences.map((preferences, index) => (
                    <MastheadPreference
                        key={index}
                        preferences={preferences}
                        logo={meta.logoGroup[index]}
                        visitorType={'Anonymous'}
                        isLoading={false}
                    />
                ))}
            </PreferenceCardContext.Provider>
            <hr />
            <StyledForm onSubmit={handleFormSubmit}>
                <StyledFormHeading>Enter your details</StyledFormHeading>
                <StyledFormSubHeading>
                    Fill out the form below to receive the selected newsletters
                    in your inbox.
                </StyledFormSubHeading>

                <StyledInputGroup>
                    <StyledLabel>
                        <StyledAsterisk>* </StyledAsterisk>First Name
                    </StyledLabel>
                    <StyledInput
                        name="firstName"
                        placeholder="Please enter first name"
                        onChange={handleFormChange}
                        disabled={formState === 'submitting'}
                        required
                    ></StyledInput>
                </StyledInputGroup>

                <StyledInputGroup>
                    <StyledLabel>
                        <StyledAsterisk>* </StyledAsterisk>Last Name
                    </StyledLabel>
                    <StyledInput
                        name="lastName"
                        placeholder="Please enter last name"
                        onChange={handleFormChange}
                        disabled={formState === 'submitting'}
                        required
                    ></StyledInput>
                </StyledInputGroup>

                <StyledInputGroup>
                    <StyledLabel>
                        <StyledAsterisk>* </StyledAsterisk>Email address
                    </StyledLabel>
                    <StyledInput
                        name="email"
                        placeholder="Please enter email address"
                        onChange={handleFormChange}
                        disabled={formState === 'submitting'}
                        required
                    ></StyledInput>
                </StyledInputGroup>
                <StyledDisclaimer>
                    By providing us with your details, you agree to our{' '}
                    <StyledWebLink display="inline" to={meta.privacyLink}>
                        Terms of Use
                    </StyledWebLink>{' '}
                    and{' '}
                    <StyledWebLink
                        display="inline"
                        to={
                            'https://www.sevenwestmedia.com.au/privacy-policies/privacy'
                        }
                    >
                        Privacy Statement
                    </StyledWebLink>
                    .
                </StyledDisclaimer>
                {formState === 'error' && (
                    <StyledError>
                        Something went wrong <br />
                        {error} <br />
                        Please try again later.
                    </StyledError>
                )}
                <StyledSubmitButton
                    type="submit"
                    disabled={
                        formState === 'submitting' ||
                        formData.optInLists.length < 1
                    }
                >
                    Subscribe to these newsletters
                </StyledSubmitButton>
            </StyledForm>
        </StyledEmailPreference>
    )
}

interface SuccessScreenProps {
    newsletters: string[]
    email: string
    contactLink: string
    token?: string
}
export const SuccessScreen: FC<SuccessScreenProps> = ({
    newsletters,
    email,
    contactLink,
    token,
}) => {
    const history = useHistory()
    function redirectHandler(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault()
        history.push(
            token
                ? `/manage-email-preferences?token=${token}`
                : '/manage-email-preferences',
        )
    }
    return (
        <>
            <StyledFormHeading>
                Welcome. <br />
                Now you'll never miss a headline.
            </StyledFormHeading>
            <StyledGreeting>Preferences for: {email}</StyledGreeting>
            <StyledDivider />
            <StyledSuccessBody>
                <StyledText>You have successfully subscribed to:</StyledText>
                {newsletters.map((newsletter, index) => (
                    <StyledBoldText key={index}>{newsletter}</StyledBoldText>
                ))}
                <StyledButton
                    isPrimary={false}
                    onClick={(e) => redirectHandler(e)}
                    width={343}
                >
                    Manage email preferences
                </StyledButton>
            </StyledSuccessBody>

            <StyledFooter>
                <h2>Looking for something else?</h2>
                <StyledWebLink to={contactLink}>Contact us</StyledWebLink>
            </StyledFooter>
        </>
    )
}
